/**
 * Phase-2 Batch B2 — useSavedSearches hook.
 *
 * Wraps `useAdminResource<SavedSearch>('/saved-searches', { responseKey:
 * 'searches' })` for the list path and adds `create(name, queryString)`
 * / `remove(id)` thin wrappers that POST/DELETE then refetch.
 *
 * The backend (Phase-2 Batch B1) scopes every CRUD verb to the current
 * user's JWT `sub` claim, so the hook intentionally does NOT pass a
 * userId — the API derives it from the bearer token. No permission
 * gate; any authenticated tenant user may manage their own rows.
 */
import { useCallback } from 'react'
import { api } from '../api/client.js'
import { useAdminResource } from './useAdminApi.js'

export interface SavedSearch {
  id: string
  userId: string
  name: string
  queryString: string
  filters: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface UseSavedSearchesResult {
  items: SavedSearch[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  create: (name: string, queryString: string) => Promise<SavedSearch>
  remove: (id: string) => Promise<void>
}

const BASE_PATH = '/saved-searches'

export function useSavedSearches(): UseSavedSearchesResult {
  const base = useAdminResource<SavedSearch>(BASE_PATH, {
    responseKey: 'searches',
  })

  // useAdminResource.refetch() is fire-and-forget (bumps a fetchKey); wrap so
  // callers can `await` after a mutation completes.
  const refetch = useCallback(async (): Promise<void> => {
    base.refetch()
  }, [base])

  const create = useCallback(
    async (name: string, queryString: string): Promise<SavedSearch> => {
      const created = await api.post<SavedSearch>(BASE_PATH, {
        name,
        queryString,
      })
      base.refetch()
      return created
    },
    [base],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`${BASE_PATH}/${id}`)
      base.refetch()
    },
    [base],
  )

  return {
    items: base.items,
    loading: base.loading,
    error: base.error,
    refetch,
    create,
    remove,
  }
}
